import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Button,
  Tag,
  Space,
  Typography,
  theme,
  message,
  Spin,
  Table,
  Tabs,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Select,
  DatePicker,
  Popconfirm,
  Statistic,
  Row,
  Col,
  Divider,
  Timeline,
  Alert,
  Tooltip,
} from "antd";
import {
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  DatabaseOutlined,
  CloudServerOutlined,
  HistoryOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import PageLayout from "../pageLayout/PageLayout.react";
import { makePagination } from "@components/common/TablePagination";

const { Text, Title } = Typography;
const { TabPane } = Tabs;

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");

export default function AemoSettlementConfig() {
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState([]);
  const [status, setStatus] = useState(null);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [duidModalVisible, setDuidModalVisible] = useState(false);
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [configForm] = Form.useForm();
  const [duidForm] = Form.useForm();
  const [syncForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState("configs");
  const [duidPageSize, setDuidPageSize] = useState(10);
  const [syncPageSize, setSyncPageSize] = useState(10);
  const [fetchedDuidPageSize, setFetchedDuidPageSize] = useState(10);
  const [syncRuns, setSyncRuns] = useState([]);
  const [syncRunsLoading, setSyncRunsLoading] = useState(false);
  const [fetchDuidsModalVisible, setFetchDuidsModalVisible] = useState(false);
  const [fetchedDuids, setFetchedDuids] = useState([]);
  const [fetchingDuids, setFetchingDuids] = useState(false);
  const [selectedFetchedDuids, setSelectedFetchedDuids] = useState([]);

  // Fetch all configs
  const fetchConfigs = useCallback(async () => {
    try {
      const authToken = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/api/aemo-settlement/configs`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await response.json();
      if (data.success) {
        setConfigs(data.data || []);
      }
    } catch (err) {
      console.error("Error fetching configs:", err);
      message.error("Failed to fetch AEMO configs");
    }
  }, []);

  // Fetch engine status
  const fetchStatus = useCallback(async () => {
    try {
      const authToken = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/api/aemo-settlement/status`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await response.json();
      if (data.success) {
        setStatus(data.data);
      }
    } catch (err) {
      console.error("Error fetching status:", err);
    }
  }, []);

  // Fetch sync runs for a config
  const fetchSyncRuns = useCallback(async (configId) => {
    if (!configId) return;
    setSyncRunsLoading(true);
    try {
      const authToken = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/api/aemo-settlement/configs/${configId}/runs?limit=50`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await response.json();
      if (data.success) {
        setSyncRuns(data.data || []);
      }
    } catch (err) {
      console.error("Error fetching sync runs:", err);
    } finally {
      setSyncRunsLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    setLoading(true);
    Promise.all([fetchConfigs(), fetchStatus()]).finally(() => setLoading(false));
  }, [fetchConfigs, fetchStatus]);

  // Fetch runs when config selected
  useEffect(() => {
    if (selectedConfig) {
      fetchSyncRuns(selectedConfig.id);
    }
  }, [selectedConfig, fetchSyncRuns]);

  // Create/Update config
  const handleSaveConfig = async (values) => {
    try {
      const authToken = localStorage.getItem("token");
      const url = editingConfig
        ? `${API_BASE}/api/aemo-settlement/configs/${editingConfig.id}`
        : `${API_BASE}/api/aemo-settlement/configs`;
      const method = editingConfig ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      });
      const data = await response.json();

      if (data.success) {
        message.success(editingConfig ? "Config updated" : "Config created");
        setConfigModalVisible(false);
        configForm.resetFields();
        setEditingConfig(null);
        fetchConfigs();
      } else {
        message.error(data.error || "Failed to save config");
      }
    } catch (err) {
      console.error("Error saving config:", err);
      message.error("Failed to save config");
    }
  };

  // Delete config
  const handleDeleteConfig = async (id) => {
    try {
      const authToken = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/api/aemo-settlement/configs/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await response.json();

      if (data.success) {
        message.success("Config deleted");
        if (selectedConfig?.id === id) {
          setSelectedConfig(null);
        }
        fetchConfigs();
      } else {
        message.error(data.error || "Failed to delete config");
      }
    } catch (err) {
      console.error("Error deleting config:", err);
      message.error("Failed to delete config");
    }
  };

  // Add DUIDs
  const handleAddDuids = async (values) => {
    if (!selectedConfig) return;
    try {
      const authToken = localStorage.getItem("token");
      const duids = values.duids.split(/[\n,]/).map((d) => d.trim()).filter(Boolean);

      const response = await fetch(`${API_BASE}/api/aemo-settlement/configs/${selectedConfig.id}/duids`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          duids: duids.map((duid) => ({
            duid,
            data_type: values.data_type || "BOTH",
          })),
        }),
      });
      const data = await response.json();

      if (data.success) {
        message.success(`Added ${data.data.inserted} DUIDs`);
        setDuidModalVisible(false);
        duidForm.resetFields();
        // Refresh config to get updated DUIDs
        const configResponse = await fetch(`${API_BASE}/api/aemo-settlement/configs/${selectedConfig.id}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const configData = await configResponse.json();
        if (configData.success) {
          setSelectedConfig(configData.data);
        }
        fetchConfigs();
      } else {
        message.error(data.error || "Failed to add DUIDs");
      }
    } catch (err) {
      console.error("Error adding DUIDs:", err);
      message.error("Failed to add DUIDs");
    }
  };

  // Trigger sync
  const handleTriggerSync = async (values) => {
    if (!selectedConfig) return;
    try {
      const authToken = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/api/aemo-settlement/configs/${selectedConfig.id}/trigger-sync`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          settlement_date: values.settlement_date.format("YYYY-MM-DD"),
          duids: values.duids,
        }),
      });
      const data = await response.json();

      if (data.success) {
        message.success(`Sync triggered for ${data.data.duidCount} DUIDs`);
        setSyncModalVisible(false);
        syncForm.resetFields();
        fetchSyncRuns(selectedConfig.id);
        fetchStatus();
      } else {
        message.error(data.error || "Failed to trigger sync");
      }
    } catch (err) {
      console.error("Error triggering sync:", err);
      message.error("Failed to trigger sync");
    }
  };

  // Fetch DUIDs from SQL Server
  const handleFetchDuidsFromServer = async () => {
    if (!selectedConfig) return;
    setFetchingDuids(true);
    setFetchedDuids([]);
    setSelectedFetchedDuids([]);
    try {
      const authToken = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/api/aemo-settlement/configs/${selectedConfig.id}/fetch-duids`, {
        method: "POST",
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await response.json();

      if (data.success) {
        // Filter out DUIDs that are already configured
        const existingDuids = new Set((selectedConfig.duids || []).map((d) => d.duid));
        const newDuids = (data.data || []).filter((d) => !existingDuids.has(d.duid));
        setFetchedDuids(newDuids);
        if (newDuids.length === 0 && data.data?.length > 0) {
          message.info("All DUIDs from SQL Server are already configured");
        } else if (newDuids.length === 0) {
          message.warning("No DUIDs found in SQL Server for the configured lookback period");
        }
        setFetchDuidsModalVisible(true);
      } else {
        message.error(data.error || "Failed to fetch DUIDs from SQL Server");
      }
    } catch (err) {
      console.error("Error fetching DUIDs:", err);
      message.error("Failed to connect to SQL Server");
    } finally {
      setFetchingDuids(false);
    }
  };

  // Add selected fetched DUIDs
  const handleAddFetchedDuids = async () => {
    if (!selectedConfig || selectedFetchedDuids.length === 0) return;
    try {
      const authToken = localStorage.getItem("token");
      const duidsToAdd = fetchedDuids.filter((d) => selectedFetchedDuids.includes(d.duid));

      const response = await fetch(`${API_BASE}/api/aemo-settlement/configs/${selectedConfig.id}/duids`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          duids: duidsToAdd.map((d) => ({
            duid: d.duid,
            region_id: d.region_id,
            data_type: "BOTH",
          })),
        }),
      });
      const data = await response.json();

      if (data.success) {
        message.success(`Added ${data.data.inserted} DUIDs`);
        setFetchDuidsModalVisible(false);
        setSelectedFetchedDuids([]);
        // Refresh config to get updated DUIDs
        const configResponse = await fetch(`${API_BASE}/api/aemo-settlement/configs/${selectedConfig.id}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const configData = await configResponse.json();
        if (configData.success) {
          setSelectedConfig(configData.data);
        }
        fetchConfigs();
      } else {
        message.error(data.error || "Failed to add DUIDs");
      }
    } catch (err) {
      console.error("Error adding DUIDs:", err);
      message.error("Failed to add DUIDs");
    }
  };

  // Open edit modal
  const openEditConfig = (config) => {
    setEditingConfig(config);
    configForm.setFieldsValue({
      ...config,
      sqlserver_password: undefined, // Don't populate password
    });
    setConfigModalVisible(true);
  };

  // Select config and fetch details
  const selectConfig = async (config) => {
    try {
      const authToken = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/api/aemo-settlement/configs/${config.id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await response.json();
      if (data.success) {
        setSelectedConfig(data.data);
      }
    } catch (err) {
      console.error("Error fetching config details:", err);
      setSelectedConfig(config);
    }
  };

  const configColumns = [
    {
      title: "Participant ID",
      dataIndex: "participant_id",
      key: "participant_id",
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          {record.participant_name && (
            <Text type="secondary" style={{ fontSize: 11 }}>{record.participant_name}</Text>
          )}
        </Space>
      ),
    },
    {
      title: "SQL Server",
      key: "sqlserver",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text code style={{ fontSize: 11 }}>{record.sqlserver_host}:{record.sqlserver_port}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{record.sqlserver_database}</Text>
        </Space>
      ),
    },
    {
      title: "Schedule",
      key: "schedule",
      render: (_, record) => (
        <Space>
          {record.schedule_enabled ? (
            <Tag color="green">Daily {String(record.schedule_hour_aest).padStart(2, "0")}:{String(record.schedule_minute).padStart(2, "0")} AEST</Tag>
          ) : (
            <Tag>Manual Only</Tag>
          )}
        </Space>
      ),
    },
    {
      title: "DUIDs",
      dataIndex: "duid_count",
      key: "duid_count",
      render: (count) => <Tag color="blue">{count || 0}</Tag>,
    },
    {
      title: "Last Sync",
      key: "last_sync",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          {record.last_sync_at ? (
            <>
              <Text style={{ fontSize: 11 }}>{dayjs(record.last_sync_at).format("YYYY-MM-DD HH:mm")}</Text>
              <Tag color={record.last_sync_status === "completed" ? "green" : record.last_sync_status === "failed" ? "red" : "blue"}>
                {record.last_sync_status || "—"}
              </Tag>
            </>
          ) : (
            <Text type="secondary">Never</Text>
          )}
        </Space>
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <Tag color={record.is_active ? "green" : "default"}>
          {record.is_active ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={(e) => { e.stopPropagation(); openEditConfig(record); }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this configuration?"
            onConfirm={() => handleDeleteConfig(record.id)}
            okText="Delete"
            okType="danger"
          >
            <Button type="text" danger icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const duidColumns = [
    {
      title: "DUID",
      dataIndex: "duid",
      key: "duid",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Name",
      dataIndex: "duid_name",
      key: "duid_name",
      render: (text) => text || "—",
    },
    {
      title: "Region",
      dataIndex: "region_id",
      key: "region_id",
      render: (text) => text ? <Tag>{text}</Tag> : "—",
    },
    {
      title: "Data Type",
      dataIndex: "data_type",
      key: "data_type",
      render: (type) => (
        <Tag color={type === "BOTH" ? "purple" : type === "DISPATCHLOAD" ? "blue" : "gold"}>
          {type}
        </Tag>
      ),
    },
    {
      title: "Last Sync",
      key: "last_sync",
      render: (_, record) => (
        record.last_sync_date ? (
          <Space direction="vertical" size={0}>
            <Text style={{ fontSize: 11 }}>{record.last_sync_date}</Text>
            <Text type="secondary" style={{ fontSize: 10 }}>
              {dayjs(record.last_sync_at).format("HH:mm")}
            </Text>
          </Space>
        ) : (
          <Text type="secondary">Never</Text>
        )
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <Tag color={record.is_active ? "green" : "default"}>
          {record.is_active ? "Active" : "Inactive"}
        </Tag>
      ),
    },
  ];

  const runColumns = [
    {
      title: "Date",
      dataIndex: "settlement_date",
      key: "settlement_date",
      render: (date) => <Text strong>{date}</Text>,
    },
    {
      title: "Type",
      dataIndex: "run_type",
      key: "run_type",
      render: (type) => (
        <Tag color={type === "SCHEDULED" ? "blue" : type === "MANUAL" ? "purple" : "orange"}>
          {type}
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const colorMap = {
          completed: "green",
          failed: "red",
          running: "processing",
          pending: "default",
        };
        return (
          <Tag color={colorMap[status] || "default"} icon={status === "running" ? <SyncOutlined spin /> : null}>
            {status}
          </Tag>
        );
      },
    },
    {
      title: "Progress",
      key: "progress",
      render: (_, record) => (
        <Text>{record.duids_processed} / {record.duids_total}</Text>
      ),
    },
    {
      title: "Rows",
      key: "rows",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 11 }}>Dispatch: {record.dispatchload_rows}</Text>
          <Text style={{ fontSize: 11 }}>FCAS: {record.fcas_payment_rows}</Text>
        </Space>
      ),
    },
    {
      title: "Time",
      key: "time",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {record.started_at ? dayjs(record.started_at).format("HH:mm:ss") : "—"}
          </Text>
          {record.completed_at && (
            <Text type="secondary" style={{ fontSize: 10 }}>
              → {dayjs(record.completed_at).format("HH:mm:ss")}
            </Text>
          )}
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <PageLayout title="AEMO Settlement Engine" icon={<CloudServerOutlined />}>
        <div style={{ textAlign: "center", padding: 100 }}>
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="AEMO Settlement Engine"
      subtitle="Configure SQL Server connections and manage AEMO data sync"
      icon={<CloudServerOutlined />}
    >
      {/* Status Overview */}
      {status && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={24}>
            <Col span={6}>
              <Statistic
                title="Scheduler"
                value={status.scheduler?.running ? "Running" : "Paused"}
                valueStyle={{ color: status.scheduler?.running ? token.colorSuccess : token.colorTextSecondary }}
                prefix={status.scheduler?.running ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
              />
            </Col>
            <Col span={4}>
              <Statistic title="Active Configs" value={status.configs?.active || 0} prefix={<DatabaseOutlined />} />
            </Col>
            <Col span={4}>
              <Statistic title="Queue Waiting" value={status.queue?.waiting || 0} />
            </Col>
            <Col span={4}>
              <Statistic title="Queue Active" value={status.queue?.active || 0} valueStyle={{ color: status.queue?.active > 0 ? token.colorPrimary : undefined }} />
            </Col>
            <Col span={6}>
              <Button icon={<ReloadOutlined />} onClick={() => { fetchStatus(); fetchConfigs(); }}>
                Refresh
              </Button>
            </Col>
          </Row>
        </Card>
      )}

      {/* Config List */}
      <Card
        title="Configurations"
        size="small"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingConfig(null); configForm.resetFields(); setConfigModalVisible(true); }}>
            Add Config
          </Button>
        }
      >
        <Table
          columns={configColumns}
          dataSource={configs}
          rowKey="id"
          size="small"
          pagination={false}
          expandable={{
            expandedRowKeys: selectedConfig ? [selectedConfig.id] : [],
            onExpand: (expanded, record) => {
              if (expanded) {
                selectConfig(record);
              } else {
                setSelectedConfig(null);
              }
            },
            expandedRowRender: () => (
              <div style={{ padding: "12px 0" }}>
                <Tabs defaultActiveKey="duids" size="small">
                  <TabPane tab={<span><DatabaseOutlined /> DUIDs ({selectedConfig?.duids?.length || 0})</span>} key="duids">
                    <Space style={{ marginBottom: 12 }}>
                      <Button icon={<PlusOutlined />} onClick={() => { duidForm.resetFields(); setDuidModalVisible(true); }}>
                        Add DUIDs
                      </Button>
                      <Button
                        icon={<CloudServerOutlined />}
                        onClick={handleFetchDuidsFromServer}
                        loading={fetchingDuids}
                      >
                        Fetch from SQL Server
                      </Button>
                      <Button icon={<PlayCircleOutlined />} type="primary" onClick={() => { syncForm.resetFields(); setSyncModalVisible(true); }}>
                        Trigger Sync
                      </Button>
                    </Space>
                    <Table
                      columns={duidColumns}
                      dataSource={selectedConfig?.duids || []}
                      rowKey="id"
                      size="small"
                      pagination={makePagination(duidPageSize, setDuidPageSize)}
                    />
                  </TabPane>
                  <TabPane tab={<span><HistoryOutlined /> Sync History</span>} key="history">
                    <Table
                      columns={runColumns}
                      dataSource={syncRuns}
                      rowKey="id"
                      size="small"
                      loading={syncRunsLoading}
                      pagination={makePagination(syncPageSize, setSyncPageSize)}
                    />
                  </TabPane>
                </Tabs>
              </div>
            ),
          }}
          onRow={(record) => ({
            onClick: () => {
              if (selectedConfig?.id === record.id) {
                setSelectedConfig(null);
              } else {
                selectConfig(record);
              }
            },
            style: { cursor: "pointer" },
          })}
        />
      </Card>

      {/* Config Modal */}
      <Modal
        title={editingConfig ? "Edit Configuration" : "New Configuration"}
        open={configModalVisible}
        onCancel={() => { setConfigModalVisible(false); setEditingConfig(null); }}
        footer={null}
        width={600}
      >
        <Form form={configForm} layout="vertical" onFinish={handleSaveConfig}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="participant_id" label="Participant ID" rules={[{ required: true }]}>
                <Input placeholder="e.g., POWOW" disabled={!!editingConfig} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="participant_name" label="Participant Name">
                <Input placeholder="e.g., Powow Energy" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ fontSize: 12 }}>SQL Server Connection</Divider>

          <Row gutter={16}>
            <Col span={16}>
              <Form.Item name="sqlserver_host" label="Host" rules={[{ required: true }]}>
                <Input placeholder="e.g., aemo-replica.example.com" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="sqlserver_port" label="Port" initialValue={1433}>
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="sqlserver_database" label="Database" rules={[{ required: true }]}>
            <Input placeholder="e.g., MMSDM" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="sqlserver_username" label="Username" rules={[{ required: !editingConfig }]}>
                <Input placeholder="Username" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="sqlserver_password" label="Password" rules={[{ required: !editingConfig }]}>
                <Input.Password placeholder={editingConfig ? "(unchanged)" : "Password"} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ fontSize: 12 }}>Schedule</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="schedule_enabled" label="Auto Sync" valuePropName="checked" initialValue={true}>
                <Switch checkedChildren="On" unCheckedChildren="Off" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="schedule_hour_aest" label="Hour (AEST)" initialValue={6}>
                <InputNumber min={0} max={23} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="schedule_minute" label="Minute" initialValue={0}>
                <InputNumber min={0} max={59} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="lookback_days" label="DUID Lookback Days" initialValue={90}>
                <InputNumber min={1} max={365} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => { setConfigModalVisible(false); setEditingConfig(null); }}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                {editingConfig ? "Update" : "Create"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Add DUIDs Modal */}
      <Modal
        title="Add DUIDs"
        open={duidModalVisible}
        onCancel={() => setDuidModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form form={duidForm} layout="vertical" onFinish={handleAddDuids}>
          <Form.Item
            name="duids"
            label="DUIDs"
            rules={[{ required: true }]}
            help="Enter DUIDs separated by commas or newlines"
          >
            <Input.TextArea rows={6} placeholder="DUID1&#10;DUID2&#10;DUID3" />
          </Form.Item>
          <Form.Item name="data_type" label="Data Type" initialValue="BOTH">
            <Select>
              <Select.Option value="BOTH">Both (Dispatch + FCAS)</Select.Option>
              <Select.Option value="DISPATCHLOAD">Dispatch Load Only</Select.Option>
              <Select.Option value="FCAS_PAYMENT">FCAS Payment Only</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => setDuidModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">Add DUIDs</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Trigger Sync Modal */}
      <Modal
        title="Trigger Manual Sync"
        open={syncModalVisible}
        onCancel={() => setSyncModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form form={syncForm} layout="vertical" onFinish={handleTriggerSync}>
          <Form.Item
            name="settlement_date"
            label="Settlement Date"
            rules={[{ required: true }]}
            initialValue={dayjs().subtract(1, "day")}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="duids"
            label="DUIDs (optional)"
            help="Leave empty to sync all configured DUIDs"
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="All DUIDs"
              options={(selectedConfig?.duids || []).map((d) => ({ value: d.duid, label: d.duid }))}
            />
          </Form.Item>
          <Alert
            type="info"
            message="Sync will pull data from SQL Server and write to Cassandra aggregation tables."
            style={{ marginBottom: 16 }}
          />
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => setSyncModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" icon={<PlayCircleOutlined />}>
                Start Sync
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Fetch DUIDs from SQL Server Modal */}
      <Modal
        title="DUIDs from SQL Server"
        open={fetchDuidsModalVisible}
        onCancel={() => { setFetchDuidsModalVisible(false); setSelectedFetchedDuids([]); }}
        footer={
          <Space>
            <Button onClick={() => { setFetchDuidsModalVisible(false); setSelectedFetchedDuids([]); }}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleAddFetchedDuids}
              disabled={selectedFetchedDuids.length === 0}
            >
              Add Selected ({selectedFetchedDuids.length})
            </Button>
          </Space>
        }
        width={600}
      >
        {fetchedDuids.length > 0 ? (
          <>
            <Alert
              type="info"
              message={`Found ${fetchedDuids.length} new DUIDs in SET_FCAS_PAYMENT (sampled from past 12 months)`}
              style={{ marginBottom: 16 }}
            />
            <Table
              rowSelection={{
                selectedRowKeys: selectedFetchedDuids,
                onChange: setSelectedFetchedDuids,
              }}
              columns={[
                {
                  title: "DUID",
                  dataIndex: "duid",
                  key: "duid",
                  render: (text) => <Text strong>{text}</Text>,
                },
                {
                  title: "Region",
                  dataIndex: "region_id",
                  key: "region_id",
                  render: (text) => text ? <Tag>{text}</Tag> : "—",
                },
              ]}
              dataSource={fetchedDuids}
              rowKey="duid"
              size="small"
              pagination={makePagination(fetchedDuidPageSize, setFetchedDuidPageSize)}
            />
            <div style={{ marginTop: 8 }}>
              <Button type="link" size="small" onClick={() => setSelectedFetchedDuids(fetchedDuids.map((d) => d.duid))}>
                Select All
              </Button>
              <Button type="link" size="small" onClick={() => setSelectedFetchedDuids([])}>
                Clear Selection
              </Button>
            </div>
          </>
        ) : (
          <Alert
            type="warning"
            message="No new DUIDs found"
            description="All DUIDs from SQL Server are already configured, or no data exists for the configured lookback period."
          />
        )}
      </Modal>
    </PageLayout>
  );
}
